import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Sponsor, SponsorAdmin } from '../types';

interface SponsorState {
  sponsors: Sponsor[];
  allSponsors: Sponsor[];
  clickCounts: Record<string, number>;
  isSponsorAdmin: boolean | null;
  sponsorAdminsList: SponsorAdmin[];

  fetchActiveSponsors: (userKommune?: string | null, userFylke?: string | null) => Promise<void>;
  fetchAllSponsorsForAdmin: () => Promise<void>;
  createSponsor: (data: Partial<Sponsor>) => Promise<string | null>;
  updateSponsor: (id: string, data: Partial<Sponsor>) => Promise<string | null>;
  deleteSponsor: (id: string) => Promise<string | null>;
  fetchClickCounts: () => Promise<void>;
  logSponsorClick: (sponsorId: string) => void;
  checkSponsorAdmin: (userId: string) => Promise<void>;
  fetchSponsorAdminsList: () => Promise<void>;
  addSponsorAdmin: (email: string) => Promise<string | null>;
  removeSponsorAdmin: (userId: string) => Promise<string | null>;
}

export const useSponsors = create<SponsorState>((set, get) => ({
  sponsors: [],
  allSponsors: [],
  clickCounts: {},
  isSponsorAdmin: null,
  sponsorAdminsList: [],

  fetchActiveSponsors: async (userKommune, userFylke) => {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from('sponsors')
      .select('*')
      .eq('active', true)
      .lte('start_date', today)
      .gte('end_date', today)
      .order('created_at');
    const matches = (s: Sponsor) => {
      const noTargeting = !s.target_kommuner?.length && !s.target_fylker?.length;
      if (noTargeting) return true;
      if (userKommune && s.target_kommuner?.includes(userKommune)) return true;
      if (userFylke && s.target_fylker?.includes(userFylke)) return true;
      return false;
    };
    set({ sponsors: (data || []).filter(matches) });
  },

  fetchAllSponsorsForAdmin: async () => {
    const { data } = await supabase.from('sponsors').select('*').order('created_at', { ascending: false }).order('id');
    set({ allSponsors: data || [] });
  },

  createSponsor: async (data) => {
    const { error } = await supabase.from('sponsors').insert(data);
    if (error) return error.message;
    await get().fetchAllSponsorsForAdmin();
    return null;
  },

  updateSponsor: async (id, data) => {
    const { error } = await supabase.from('sponsors').update(data).eq('id', id);
    if (error) return error.message;
    await get().fetchAllSponsorsForAdmin();
    return null;
  },

  deleteSponsor: async (id) => {
    const { error } = await supabase.from('sponsors').delete().eq('id', id);
    if (error) return error.message;
    await get().fetchAllSponsorsForAdmin();
    return null;
  },

  fetchClickCounts: async () => {
    const { data } = await supabase.from('sponsor_clicks').select('sponsor_id');
    const counts: Record<string, number> = {};
    (data || []).forEach((row: { sponsor_id: string }) => {
      counts[row.sponsor_id] = (counts[row.sponsor_id] || 0) + 1;
    });
    set({ clickCounts: counts });
  },

  logSponsorClick: (sponsorId) => {
    supabase.from('sponsor_clicks').insert({ sponsor_id: sponsorId }).then(() => {});
  },

  checkSponsorAdmin: async (userId) => {
    const { data } = await supabase.from('sponsor_admins').select('user_id').eq('user_id', userId).maybeSingle();
    set({ isSponsorAdmin: !!data });
  },

  fetchSponsorAdminsList: async () => {
    const { data, error } = await supabase.rpc('list_sponsor_admins');
    if (!error) set({ sponsorAdminsList: data || [] });
  },

  addSponsorAdmin: async (email) => {
    const { error } = await supabase.rpc('add_sponsor_admin', { target_email: email });
    if (error) return error.message;
    await get().fetchSponsorAdminsList();
    return null;
  },

  removeSponsorAdmin: async (userId) => {
    const { error } = await supabase.rpc('remove_sponsor_admin', { target_user_id: userId });
    if (error) return error.message;
    await get().fetchSponsorAdminsList();
    return null;
  },
}));
